import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Send, RefreshCw, Inbox, ChevronLeft, PenLine, Plus, Trash2, Settings, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } });
const axGet = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function AdminEmailPage() {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Compose
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);

  // Account management
  const [showAccountMgmt, setShowAccountMgmt] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [newAcc, setNewAcc] = useState({ email: '', password: '', label: '', smtp_host: 'smtp9.gmoserver.jp', smtp_port: 587, pop_host: 'pop9.gmoserver.jp', pop_port: 995 });
  const [testingId, setTestingId] = useState(null);

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/email/accounts`, axGet());
      const data = await res.json();
      setAccounts(data);
      if (data.length > 0 && !selectedAccount) {
        setSelectedAccount(data[0].id);
        fetchInbox(data[0].id);
      }
    } catch (e) { console.error(e); }
  };

  const fetchInbox = async (accountId) => {
    if (!accountId) return;
    setLoading(true);
    setSelectedEmail(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/email/inbox/${accountId}?limit=30`, axGet());
      const data = await res.json();
      if (data.emails) setEmails(data.emails);
      else { setEmails([]); if (data.detail) toast.error(data.detail); }
    } catch (e) {
      toast.error(t('admin_email_fetch_fail'));
      setEmails([]);
    } finally { setLoading(false); }
  };

  const handleAccountChange = (val) => {
    setSelectedAccount(val);
    fetchInbox(val);
  };

  const handleSend = async () => {
    if (!composeTo || !composeSubject || !composeBody) { toast.error(t('admin_email_fill_all')); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email/send`, {
        method: 'POST', ...ax(),
        body: JSON.stringify({ to: composeTo, subject: composeSubject, body: composeBody, account: selectedAccount }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(t('admin_email_sent')); setComposeOpen(false); setComposeTo(''); setComposeSubject(''); setComposeBody(''); }
      else toast.error(data.detail || 'Failed');
    } catch (e) { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const handleAddAccount = async () => {
    if (!newAcc.email || !newAcc.password) { toast.error(t('admin_email_fill_all')); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/email/accounts`, {
        method: 'POST', ...ax(),
        body: JSON.stringify(newAcc),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(t('admin_email_account_added'));
        setAddAccountOpen(false);
        setNewAcc({ email: '', password: '', label: '', smtp_host: 'smtp9.gmoserver.jp', smtp_port: 587, pop_host: 'pop9.gmoserver.jp', pop_port: 995 });
        fetchAccounts();
      } else toast.error(data.detail || 'Failed');
    } catch (e) { toast.error('Error'); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm(t('admin_email_delete_confirm'))) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/email/accounts/${id}`, { method: 'DELETE', ...axGet() });
      if (res.ok) {
        toast.success(t('admin_email_account_deleted'));
        if (selectedAccount === id) setSelectedAccount('');
        fetchAccounts();
      }
    } catch (e) { toast.error('Error'); }
  };

  const handleTestAccount = async (id) => {
    setTestingId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/email/accounts/${id}/test`, { method: 'POST', ...axGet() });
      const data = await res.json();
      if (res.ok) toast.success(t('admin_email_test_ok'));
      else toast.error(data.detail || 'Failed');
    } catch (e) { toast.error('Connection failed'); }
    finally { setTestingId(null); }
  };

  const openReply = (email) => {
    setComposeTo(email.from.replace(/<|>/g, '').split(' ').pop());
    setComposeSubject(`Re: ${email.subject}`);
    setComposeBody(`\n\n---\n${email.body_text}`);
    setComposeOpen(true);
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleString('ja-JP'); } catch { return d; } };
  const currentAccountEmail = accounts.find(a => a.id === selectedAccount)?.email || '';

  return (
    <AdminLayout>
      <div data-testid="admin-email-page">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-primary mb-1">{t('admin_email_title')}</h1>
            <p className="text-muted-foreground text-sm">{t('admin_email_desc')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowAccountMgmt(!showAccountMgmt)} data-testid="manage-accounts-btn">
              <Settings className="h-4 w-4 mr-1.5" />{t('admin_email_manage_accounts')}
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => fetchInbox(selectedAccount)} disabled={loading} data-testid="refresh-inbox-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />{t('admin_email_refresh')}
            </Button>
            <Button size="sm" className="rounded-full" onClick={() => { setComposeOpen(true); setComposeTo(''); setComposeSubject(''); setComposeBody(''); }} data-testid="compose-email-btn">
              <PenLine className="h-4 w-4 mr-1.5" />{t('admin_email_compose')}
            </Button>
          </div>
        </div>

        {/* Account Management Panel */}
        {showAccountMgmt && (
          <div className="bg-card border border-border/50 rounded-2xl p-6 mb-6" data-testid="account-management-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">{t('admin_email_accounts_title')}</h3>
              <Button size="sm" className="rounded-full" onClick={() => setAddAccountOpen(true)} data-testid="add-account-btn">
                <Plus className="h-4 w-4 mr-1" />{t('admin_email_add_account')}
              </Button>
            </div>
            <div className="space-y-3">
              {accounts.map(acc => (
                <div key={acc.id} className="flex items-center justify-between bg-muted/30 rounded-xl px-4 py-3" data-testid={`account-row-${acc.id}`}>
                  <div>
                    <p className="font-medium text-sm">{acc.email}</p>
                    <p className="text-xs text-muted-foreground">{acc.label} | SMTP: {acc.smtp_host}:{acc.smtp_port} | POP: {acc.pop_host}:{acc.pop_port}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => handleTestAccount(acc.id)} disabled={testingId === acc.id} data-testid={`test-account-${acc.id}`}>
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />{testingId === acc.id ? '...' : t('admin_email_test')}
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDeleteAccount(acc.id)} data-testid={`delete-account-${acc.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {accounts.length === 0 && <p className="text-center text-muted-foreground text-sm py-4">{t('admin_email_no_accounts')}</p>}
            </div>
          </div>
        )}

        {/* Account Selector */}
        {accounts.length > 0 && (
          <div className="mb-6">
            <Select value={selectedAccount} onValueChange={handleAccountChange}>
              <SelectTrigger className="w-full sm:w-80" data-testid="account-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {accounts.map(acc => (
                  <SelectItem key={acc.id} value={acc.id}>
                    <span className="flex items-center gap-2"><Mail className="h-4 w-4" />{acc.email}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Email List / Detail */}
        <div className="bg-card border border-border/50 rounded-2xl overflow-hidden min-h-[500px]">
          {selectedEmail ? (
            <div data-testid="email-detail-view">
              <div className="border-b px-6 py-3 flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)} data-testid="back-to-inbox-btn">
                  <ChevronLeft className="h-4 w-4 mr-1" />{t('admin_email_back')}
                </Button>
                <Button variant="outline" size="sm" className="rounded-full ml-auto" onClick={() => openReply(selectedEmail)} data-testid="reply-email-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" />{t('admin_email_reply')}
                </Button>
              </div>
              <div className="px-6 py-4 border-b bg-muted/20">
                <h2 className="font-heading text-lg font-semibold mb-2" data-testid="email-detail-subject">{selectedEmail.subject}</h2>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><span className="font-medium text-foreground">{t('admin_email_from')}:</span> {selectedEmail.from}</p>
                  <p><span className="font-medium text-foreground">{t('admin_email_to')}:</span> {selectedEmail.to}</p>
                  <p><span className="font-medium text-foreground">{t('admin_email_date')}:</span> {formatDate(selectedEmail.date)}</p>
                </div>
              </div>
              <div className="px-6 py-5">
                {selectedEmail.has_html ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} data-testid="email-detail-body" />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed" data-testid="email-detail-body">{selectedEmail.body_text}</pre>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="border-b px-4 sm:px-6 py-3 bg-muted/20 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium min-w-0">
                  <Inbox className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{currentAccountEmail} — {emails.length} {t('admin_email_messages')}</span>
                </div>
                <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0 ml-2" onClick={() => fetchInbox(selectedAccount)} disabled={loading} data-testid="inbox-refresh-btn">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-16 text-muted-foreground">{t('loading')}</div>
              ) : emails.length === 0 ? (
                <div className="text-center py-16">
                  <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-40" />
                  <p className="text-muted-foreground">{t('admin_email_empty')}</p>
                </div>
              ) : (
                <div className="divide-y" data-testid="email-inbox-list">
                  {emails.map((email) => (
                    <div key={email.id} className="px-6 py-3 hover:bg-muted/30 cursor-pointer smooth-transition flex items-start gap-4"
                      onClick={() => setSelectedEmail(email)} data-testid={`email-row-${email.id}`}>
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Mail className="h-4 w-4 text-accent" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-sm font-medium truncate">{email.from}</p>
                          <span className="text-xs text-muted-foreground flex-shrink-0">{formatDate(email.date)}</span>
                        </div>
                        <p className="text-sm font-semibold text-primary truncate">{email.subject}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{email.body_text?.substring(0, 100)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Compose Dialog */}
        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('admin_email_compose')}</DialogTitle>
              <DialogDescription>{t('admin_email_compose_desc')} ({currentAccountEmail})</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4" data-testid="compose-email-form">
              <div className="space-y-1.5">
                <Label>{t('admin_email_to')}</Label>
                <Input value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="recipient@example.com" data-testid="compose-to-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('admin_email_subject')}</Label>
                <Input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} data-testid="compose-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('admin_email_body')}</Label>
                <Textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={10} data-testid="compose-body-input" />
              </div>
              <Button onClick={handleSend} disabled={sending} className="w-full rounded-full" data-testid="send-email-btn">
                <Send className="h-4 w-4 mr-2" />{sending ? t('admin_email_sending') : t('admin_email_send')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Account Dialog */}
        <Dialog open={addAccountOpen} onOpenChange={setAddAccountOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('admin_email_add_account')}</DialogTitle>
              <DialogDescription>{t('admin_email_add_account_desc')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4" data-testid="add-account-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('admin_email_address')} *</Label>
                  <Input value={newAcc.email} onChange={e => setNewAcc(p => ({ ...p, email: e.target.value }))} placeholder="user@prcremit.com" data-testid="new-account-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('admin_email_password')} *</Label>
                  <Input type="password" value={newAcc.password} onChange={e => setNewAcc(p => ({ ...p, password: e.target.value }))} data-testid="new-account-password" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t('admin_email_label')}</Label>
                <Input value={newAcc.label} onChange={e => setNewAcc(p => ({ ...p, label: e.target.value }))} placeholder="info" data-testid="new-account-label" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>SMTP {t('admin_email_server')}</Label>
                  <Input value={newAcc.smtp_host} onChange={e => setNewAcc(p => ({ ...p, smtp_host: e.target.value }))} data-testid="new-account-smtp" />
                </div>
                <div className="space-y-1.5">
                  <Label>SMTP {t('admin_email_port')}</Label>
                  <Input type="number" value={newAcc.smtp_port} onChange={e => setNewAcc(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))} data-testid="new-account-smtp-port" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>POP {t('admin_email_server')}</Label>
                  <Input value={newAcc.pop_host} onChange={e => setNewAcc(p => ({ ...p, pop_host: e.target.value }))} data-testid="new-account-pop" />
                </div>
                <div className="space-y-1.5">
                  <Label>POP {t('admin_email_port')}</Label>
                  <Input type="number" value={newAcc.pop_port} onChange={e => setNewAcc(p => ({ ...p, pop_port: parseInt(e.target.value) || 995 }))} data-testid="new-account-pop-port" />
                </div>
              </div>
              <Button onClick={handleAddAccount} className="w-full rounded-full" data-testid="save-account-btn">
                <Plus className="h-4 w-4 mr-2" />{t('admin_email_add_account')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
